import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { MessagePopup, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import Page from '@/components/content/Page'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import EcomOrderDealerInstructions from '@/components/shop/orders/order/EcomOrderDealerInstructions'
import LoaderCircle from '@/components/util/LoaderCircle'
import {
  fetchEcomOrder,
  getEcomOrder,
  getEcomOrderProducts,
  getIsLoaded,
  getIsLoading,
  setEcomOrderStatus,
} from '@/ducks/ecomOrderReducer'
import { getShopOrderStatusTextId } from '@/util/orders'
import { ECOM_STATUS } from '@/util/orders'

import EcomOrderDetails from './EcomOrderDetails'
import EcomOrderInputs from './EcomOrderInputs'
import EcomOrderPageOptions from './EcomOrderPageOptions'
import EcomOrderProducts from './EcomOrderProducts'
import EcomOrderUser from './EcomOrderUser'

import './ecom-order-page.scss'

const EcomOrderPage: FC = () => {
  const { whiteLabel } = useAppContext()

  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false)

  const params = useParams()
  const orderId = parseInt(params.order_id!)
  const dispatch = useDispatch()
  const translate = useTranslation()

  const isLoading = useSelector(getIsLoading)
  // @ts-ignore
  const isLoaded = useSelector(getIsLoaded)
  const order = useSelector(getEcomOrder)
  const products = useSelector(getEcomOrderProducts)

  useEffect(() => {
    // @ts-ignore
    dispatch(fetchEcomOrder(orderId))
  }, [])

  useEffect(() => {
    if (!isLoaded) return
    // legacy redux setup keeping the old order in the store
    if (orderId !== order.id) return
    // The only state where the order is eligible for picking
    if (!order.isInManualProcessing && !order.isInAutomaticProcessing) setIsPopupOpen(true)
    // there is a problem with redux caching thus the hack
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded])

  const totalCost = products.reduce(
    (acc: number, cur: { amount: number; quantity: number }) => acc + cur.amount * cur.quantity,
    0
  )

  return (
    <Page className="ecom-order-page">
      <div className="flex items-center gap-2">
        <h1>
          <Translation id="Shop Order {orderId}" orderId={String(orderId)} />
        </h1>

        {(order.isInAutomaticProcessing || order.isInManualProcessing) && (
          <div
            data-tooltip-id={`order-automation-indicator-${orderId}`}
            data-tooltip-content={translate(
              order.isInAutomaticProcessing
                ? 'This order is an automated order.'
                : 'This order is for manual handling.'
            )}
            className={twJoin(
              'bold mb-4 flex h-7 w-7 items-center justify-center rounded-full text-lg font-semibold text-white',
              order.isInManualProcessing && 'bg-skytech-lightOrange',
              order.isInAutomaticProcessing && 'bg-green-600'
            )}
          >
            <div>{order.isInAutomaticProcessing ? 'A' : 'M'}</div>
          </div>
        )}

        <Tooltip id={`order-automation-indicator-${orderId}`} style={{ zIndex: 20 }} size="large" />
      </div>

      {isLoading && !isLoaded ? (
        <LoaderCircle />
      ) : (
        <>
          <h2>
            <Translation id={getShopOrderStatusTextId(order?.status)} />
          </h2>

          <EcomOrderPageOptions
            isViewOnlyMode={isViewOnlyMode}
            onSetIsViewOnlyMode={(val: boolean) => setIsViewOnlyMode(val)}
          />

          <EcomOrderDealerInstructions />

          <div className="sections">
            <EcomOrderDetails {...order} totalCost={totalCost} />

            <EcomOrderUser />

            <EcomOrderInputs isViewOnlyMode={isViewOnlyMode} />

            <EcomOrderProducts
              isViewOnlyMode={isViewOnlyMode}
              products={products}
              currency={order.currency}
            />
          </div>
        </>
      )}

      <MessagePopup
        type="warning"
        title={<Translation className="capitalize" id="Manual processing" />}
        message={
          <Translation id="This is a new order that has not yet been handled by automated workflows. By choosing to process the order manually, it will be excluded from further handling through automation or API. Do you want to proceed with manual processing?" />
        }
        isOpen={isPopupOpen}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      >
        <div className="mt-2 flex items-center justify-end">
          <Button
            className="mb-0"
            variant="tertiary"
            onClick={() => {
              setIsViewOnlyMode(true)
              setIsPopupOpen(false)
            }}
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
          >
            <Translation id="View the order" />
          </Button>

          <Button
            className="mb-0"
            isLoading={isLoading}
            onClick={() => {
              // @ts-ignore
              dispatch(setEcomOrderStatus(ECOM_STATUS.ACCEPTED, () => setIsPopupOpen(false)))
            }}
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
          >
            <Translation id="Pick order for processing" />
          </Button>
        </div>
      </MessagePopup>
    </Page>
  )
}

export default EcomOrderPage
